import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { opportunityContactsQueryKeys } from "app/pages/sales-intelligence/pages/opportunity-list/query/queryKeyFactory";
import {
    retryDelayForSearchResults,
    retrySearchResultsQuery,
    throwOnUnhandledSearchError,
} from "app/pages/sales-intelligence/query/utils";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import type { ContactsResponseDto } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/dto";
import { assertDefined } from "app/pages/sales-intelligence/utils/errors/assertions";
import { errorOperationMap } from "app/pages/sales-intelligence/utils/errors/context";
import type { PostConfigObject } from "app/services/fetchService";

import type { FetchContactsParams } from "../../services/api/types";

interface IUseOpportunityContactsSearchResultsQueryParams<T> extends FetchContactsParams {
    enabled: boolean;
    config?: PostConfigObject;
    select?: (data: ContactsResponseDto) => T;
    throwOnError?: boolean | ((error: unknown) => boolean);
}

const useOpportunityContactsSearchResultsQuery = <T = ContactsResponseDto>({
    listParams,
    leadsType,
    dto,
    enabled,
    config,
    select,
    throwOnError = throwOnUnhandledSearchError,
}: IUseOpportunityContactsSearchResultsQueryParams<T>) => {
    return useQuery<ContactsResponseDto, unknown, T>({
        enabled: enabled,
        staleTime: Number.POSITIVE_INFINITY,
        queryKey: opportunityContactsQueryKeys.tableData(listParams.id, leadsType, dto),
        placeholderData: keepPreviousData,
        select,
        throwOnError,
        retry: retrySearchResultsQuery,
        retryDelay: retryDelayForSearchResults,
        async queryFn() {
            const response = await salesApiService.opportunityListPage.fetchContacts(
                {
                    listParams,
                    dto,
                    leadsType,
                },
                config,
            );

            assertDefined(response, {
                name: "fetchSearchResultsResponse",
                operation: errorOperationMap.emptySearchResults,
            });

            return response;
        },
    });
};

export default useOpportunityContactsSearchResultsQuery;
